// The pirate dens of the reader (v2-the-deep.md §8.5, R2720, ruling R-f):
// `Empire.dens`, under the flag `pirates`.
//
// The server lists each den the faction of the reader knows, and each den
// whose chart fragment the reader holds, in den id order. `respawn_sec`
// counts the world seconds until a sunk group sails again, rounded up, at
// the moment the server sent the frame. The client counts it down on the
// world clock (`GameState.seasonElapsedSec`): the frame is anchored when a
// reader first sees it, and the server sends a new frame when a den
// changes. The den layer and the den card read this one module.

import { DenCache, DenState } from './gen/proto/aquaphobia';
import type { ChartFragment, DenInfo, Empire } from './gen/proto/aquaphobia';
import type { GameState } from './state';
import { cellCenterX, cellCenterY } from './world';
import { denDesignOf, type DenCacheMark, type DenDesign, type DenForm } from './render/den-marks';

/** What the den module reads of the store. */
export type DenSource = Pick<GameState, 'empire' | 'hasFeatureFlag' | 'seasonElapsedSec'>;

const NO_DENS: readonly DenInfo[] = [];
const NO_FRAGMENTS: readonly ChartFragment[] = [];

/** The dens of the reader; none while the flag `pirates` of the NPC side
 *  is off (R2622). */
export function denRows(s: DenSource): readonly DenInfo[] {
  return s.hasFeatureFlag('pirates') ? s.empire?.dens ?? NO_DENS : NO_DENS;
}

/** The chart fragments of the reader; none while `pirates` is off. */
export function fragmentRows(s: DenSource): readonly ChartFragment[] {
  return s.hasFeatureFlag('pirates') ? s.empire?.chartFragments ?? NO_FRAGMENTS : NO_FRAGMENTS;
}

/** The season clock at which a reader first saw each Empire frame. */
const anchors = new WeakMap<Empire, number>();

/** The world seconds until the group of a sunk den sails again, now. */
export function denLeftSec(s: DenSource, d: DenInfo): number {
  if (d.state !== DenState.DEN_STATE_SUNK || !(d.respawnSec > 0)) return 0;
  const now = s.seasonElapsedSec();
  const e = s.empire;
  let at = now;
  if (e) {
    const seen = anchors.get(e);
    if (seen === undefined) anchors.set(e, now); else at = seen;
  }
  return Math.max(0, d.respawnSec - Math.max(0, now - at));
}

/** The form of a den state; null for a state the wire does not name. */
export function denForm(state: DenState): DenForm | null {
  switch (state) {
    case DenState.DEN_STATE_HOME: return 'home';
    case DenState.DEN_STATE_AT_SEA: return 'sea';
    case DenState.DEN_STATE_SUNK: return 'sunk';
    default: return null;
  }
}

/** The cache mark of a den row. */
export function denCacheMark(c: DenCache): DenCacheMark {
  return c === DenCache.DEN_CACHE_SEEN ? 'seen' : c === DenCache.DEN_CACHE_TAKEN ? 'taken' : 'none';
}

/** The design of a den row, or null. */
export function denDesign(d: DenInfo): DenDesign | null {
  return denDesignOf(d.brotherhood, d.design);
}

/**
 * The den whose body holds a world point, or null. A den the layer does
 * not draw (no design, no state) is not picked. Where two bodies hold the
 * point, the den whose centre is nearer wins. `margin` grows the body box
 * on every side, in world px: the touch margin of a finger (R2851).
 */
export function denAt(s: DenSource, x: number, y: number, margin = 0): DenInfo | null {
  let best: DenInfo | null = null, bestD = Infinity;
  for (const d of denRows(s)) {
    const des = denDesign(d);
    if (!des || !denForm(d.state)) continue;
    const dx = x - cellCenterX(d.cell), dy = y - cellCenterY(d.cell);
    const b = des.box;
    if (dx < b[0] - margin || dx > b[2] + margin || dy < b[1] - margin || dy > b[3] + margin) continue;
    const dd = dx * dx + dy * dy;
    if (dd < bestD) { best = d; bestD = dd; }
  }
  return best;
}
